import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MarketService } from "../../../../../../../shared/src/lib/services/market.service";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { AsyncPipe, DatePipe, DecimalPipe, NgClass } from "@angular/common";
import { ReportService } from "../../../../../../../shared/src/lib/services/report.service";
import { AlertComponent } from "../../../../components/alert/alert.component";
import { ApiService } from "../../../../../../../shared/src";
import { TranslateModule } from "@ngx-translate/core";
import { Observable, Subject } from "rxjs";
import { CoreService } from "../../../../../../../shared/src/lib/services/core.service";
import { ActivatedRoute, Router } from "@angular/router";
import { ReportUtilsService } from "../report-utils.service";
import { CapitalizeFirstPipe } from "../../../../../../../shared/src/lib/pipe/capitalize-first.pipe";
import { LoaderService } from "../../../../../../../shared/src/lib/services/loader.service";
import { ReportLoaderComponent } from "../report-loader/report-loader.component";
import { downloadTableAsExcel } from "../../../../../../../shared/src/lib/utils/common";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { NgxPrintModule } from "ngx-print";
import { BalanceService } from "../../../../../../../shared/src/lib/services/balance.service";
import { ExRateService } from "../../../../../../../shared/src/lib/services/ex-rate.service";
import { MatDatepickerModule } from "@angular/material/datepicker";
import dayjs from "dayjs";
import { takeUntil } from "rxjs/operators";
import { getDateRangeFromReference, today } from "../../../../../../../shared/src/lib/utils/date-range";

@Component({
  selector: "app-statement",
  imports: [
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    AlertComponent,
    DecimalPipe,
    TranslateModule,
    NgClass,
    FormsModule,
    AsyncPipe,
    CapitalizeFirstPipe,
    ReportLoaderComponent,
    MatButton,
    MatMenuModule,
    NgxPrintModule,
    MatDatepickerModule,
    MatIcon,
    DatePipe,
  ],
  providers: [DatePipe, DecimalPipe, CapitalizeFirstPipe],
  templateUrl: "./statement.component.html",
  styleUrls: ["./statement.component.scss", "../report.scss"],
})
export class StatementComponent implements OnInit, OnDestroy {
  readonly destroy$ = new Subject<void>();
  Math = Math;
  columns = ["Bet ID", "Settled", "Matched", "Description", "Type", "Odds", "Price", "Stake", "Status", "Debit", "Credit", "Balance"];
  loader = inject(LoaderService);
  marketService = inject(MarketService);
  balanceService = inject(BalanceService);
  reportService = inject(ReportService);
  apiService = inject(ApiService);
  coreService = inject(CoreService);
  exRateService = inject(ExRateService);
  reportUtils = inject(ReportUtilsService);
  balanceData: any = {
    BALANCE: 0,
  };
  statements: any[] = [];
  form: FormGroup;
  $loading!: Observable<boolean>;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.form = this.fb.group({
      WALLET_ID: [+this.route.snapshot.queryParams["WALLET_ID"] || ""],
      RANGE: [+this.route.snapshot.queryParams["RANGE"] || 3],
      FROM_DATE: [dayjs(this.route.snapshot.queryParams["FROM_DATE"]).format("YYYY-MM-DD")],
      TO_DATE: [dayjs(this.route.snapshot.queryParams["TO_DATE"]).format("YYYY-MM-DD")],
    });
  }

  get range() {
    return this.form.get("RANGE");
  }

  get fromDate() {
    return this.form.get("FROM_DATE");
  }

  get toDate() {
    return this.form.get("TO_DATE");
  }

  get walletId() {
    return this.form.get("WALLET_ID");
  }

  ngOnInit() {
    this.$loading = this.loader.isLoading("/v1/api/reports/balance_statement");

    this.toDate?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      const customRange = this.rangeSelection({
        FROM_DATE: this.fromDate?.value,
        TO_DATE: value,
      });
      this.updateQueryParam({
        ...this.form.value,
        ...customRange,
        WALLET_ID: this.walletId?.value,
      });
    });

    this.range?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value !== 8) {
        const range = this.reportUtils.dateRange(value);
        this.form.patchValue(range);
      }
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params: any) => {
      if (params["WALLET_ID"] && params["FROM_DATE"] && params["TO_DATE"]) {
        this.fetchBalanceStatement({
          RANGE: +params["RANGE"],
          WALLET_ID: +params["WALLET_ID"],
          FROM_DATE: params["FROM_DATE"],
          TO_DATE: params["TO_DATE"],
        });
      }
    });
    this.getBalance();
    this.subscribeToStatement();
  }

  resetDateRange() {
    this.form.patchValue({ FROM_DATE: "", TO_DATE: "" });
  }

  subscribeToStatement() {
    this.reportService.statement$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        // console.log(response);
        if (response?.result) {
          this.statements = response?.result;
        }
      },
      error: (error: any) => {
        console.error(error);
      },
    });
  }

  onDateSelection() {
    this.range?.setValue(8);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  rangeSelection(form: any) {
    if (form?.FROM_DATE && form?.TO_DATE) {
      return { FROM_DATE: dayjs(form?.FROM_DATE).format("DD-MMM-YYYY"), TO_DATE: dayjs(form?.TO_DATE).format("DD-MMM-YYYY") };
    } else {
      return { FROM_DATE: "", TO_DATE: "" };
    }
  }

  updateQueryParam(values: any) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: values,
      queryParamsHandling: "merge",
    });
  }

  getBalance() {
    this.balanceService.balanceData$.pipe(takeUntil(this.destroy$)).subscribe((balance: any) => {
      if (balance) {
        this.form.patchValue({
          WALLET_ID: balance?.ID || "",
          RANGE: this.range?.value,
        });
        this.balanceData = balance;
      }
    });
  }

  fetchBalanceStatement(params: any) {
    const { RANGE, ...rest } = params || {};
    this.reportService.fetchAccountStatement(rest);
  }

  handleDownload() {
    downloadTableAsExcel("account-statement-table", "account statement report");
  }

  protected readonly getDateRangeFromReference = getDateRangeFromReference;
  protected readonly today = today;
}
